import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  FormGroup,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { AuthButton, AuthInput } from "../../auth/style";
import StoreWhatsappOtpHint from "../components/StoreWhatsappOtpHint";
import { setAuthenticatedUser } from "../../auth/service";
import { AppContext } from "../../context";
import storeApi from "../storeApi";

const DealerSignupRegister = () => {
  const navigate = useNavigate();
  const { setCurrentUser, setDealerReferral } = useContext(AppContext) || {};
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [error, setError] = useState(null);
  const [sendOtpAttempted, setSendOtpAttempted] = useState(false);

  const toErrorText = (err, fallback) => {
    const raw =
      err?.message ??
      err?.error ??
      err?.response?.data?.message ??
      err?.response?.data?.error?.message ??
      err?.response?.data?.error ??
      fallback;
    if (typeof raw === "string") return raw;
    if (raw && typeof raw === "object") {
      if (typeof raw.message === "string") return raw.message;
      if (typeof raw.error === "string") return raw.error;
      try {
        return JSON.stringify(raw);
      } catch {
        return fallback;
      }
    }
    return String(raw ?? fallback);
  };

  const isValidMobile = (value) => value.length === 10 && /^\d+$/.test(value);
  const isValidName = (value) => value.trim().length >= 2;
  const isValidCompanyName = (value) => value.trim().length >= 2;

  const canSendOtp =
    isValidName(name) &&
    isValidCompanyName(companyName) &&
    isValidMobile(mobile.trim()) &&
    !isSendingOtp &&
    !otpSent;

  const canVerifyOtp = otpSent && otp.trim().length > 0 && !isVerifyingOtp;

  const handleChangeMobile = () => {
    setOtpSent(false);
    setOtp("");
    setError(null);
    setSendOtpAttempted(false);
  };

  const handleSendOtp = async () => {
    setSendOtpAttempted(true);
    if (!canSendOtp) return;

    setIsSendingOtp(true);
    setError(null);
    try {
      await storeApi.dealerAuthAPI.generateOtp({
        mobile: mobile.trim(),
      });
      setOtpSent(true);
    } catch (e) {
      setError(toErrorText(e, "Unable to send OTP. Please try again."));
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!canVerifyOtp) return;

    setIsVerifyingOtp(true);
    setError(null);
    try {
      const response = await storeApi.dealerAuthAPI.register({
        mobile: mobile.trim(),
        name: name.trim(),
        company_name: companyName.trim(),
        otp_code: otp.trim(),
      });

      const data = response?.data || response;
      const user = data?.user || null;
      const token = data?.token || null;
      if (!user || !token) {
        setError("Dealer signup succeeded but session data missing.");
        return;
      }

      window.localStorage.setItem("storeAccessToken", token);
      setAuthenticatedUser({ token, user });
      if (typeof setCurrentUser === "function") setCurrentUser(user);

      try {
        const session = await storeApi.sessionAPI.getCurrentSession();
        const referral = session?.dealer_referral || null;
        if (typeof setDealerReferral === "function") setDealerReferral(referral);
      } catch (_e) {
        // Non-fatal.
      }

      navigate("/dealer/dashboard", { replace: true });
    } catch (e) {
      setError(toErrorText(e, "Invalid OTP. Please try again."));
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6 col-xl-5">
          <h3 className="fw-bold mb-2">Dealer Registration</h3>

          <Form>
            <FormGroup noMargin className="mb-4">
              <AuthInput
                disabled={otpSent}
                invalid={sendOtpAttempted && !isValidCompanyName(companyName)}
                type="text"
                value={companyName}
                placeholder="Company / Business Name"
                onChange={(e) => setCompanyName(e.target.value)}
                className="unmask"
              />
              <FormFeedback>
                Company name must contain at least 2 characters.
              </FormFeedback>
            </FormGroup>

            <FormGroup noMargin className="mb-4">
              <AuthInput
                disabled={otpSent}
                invalid={sendOtpAttempted && !isValidName(name)}
                type="text"
                value={name}
                placeholder="Your Name"
                onChange={(e) => setName(e.target.value)}
                className="unmask"
              />
              <FormFeedback>
                Name must contain at least 2 characters.
              </FormFeedback>
            </FormGroup>

            <FormGroup noMargin className="mb-4">
              <AuthInput
                disabled={otpSent}
                invalid={sendOtpAttempted && !isValidMobile(mobile)}
                type="tel"
                value={mobile}
                placeholder="Mobile Number"
                onChange={(e) => setMobile(e.target.value)}
                className="mt-1 unmask"
              />
              {!otpSent ? (
                <StoreWhatsappOtpHint variant="will_be_sent" />
              ) : null}
              {sendOtpAttempted && !isValidMobile(mobile) && !otpSent ? (
                <FormFeedback>
                  Please enter a valid 10 digit mobile number.
                </FormFeedback>
              ) : null}
              {otpSent ? (
                <div className="d-block mt-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none text-link small"
                    onClick={handleChangeMobile}
                  >
                    <i className="fa fa-edit" aria-hidden /> Change
                  </button>
                </div>
              ) : null}
            </FormGroup>

            {otpSent && (
              <FormGroup noMargin className="mb-4">
                <AuthInput
                  type="text"
                  value={otp}
                  placeholder="Enter OTP"
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  className="unmask"
                />
                <StoreWhatsappOtpHint
                  variant="sent_to"
                  mobile={mobile}
                  className="d-block mt-2 pb-1"
                />
              </FormGroup>
            )}

            {error ? (
              <Alert color="danger" className="mb-4">
                <span>{error}</span>
              </Alert>
            ) : null}

            <div className="d-grid gap-2">
              {!otpSent ? (
                <AuthButton
                  type="button"
                  onClick={handleSendOtp}
                  disabled={isSendingOtp || otpSent}
                  color="primary"
                >
                  {isSendingOtp ? (
                    <>
                      <Spinner size="sm">Loading...</Spinner>{" "}
                      <span>Sending OTP...</span>
                    </>
                  ) : (
                    "Continue"
                  )}
                </AuthButton>
              ) : (
                <AuthButton
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={!canVerifyOtp}
                  color="primary"
                >
                  {isVerifyingOtp ? (
                    <>
                      <Spinner size="sm">Loading...</Spinner>{" "}
                      <span>Verifying...</span>
                    </>
                  ) : (
                    "Continue"
                  )}
                </AuthButton>
              )}
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default DealerSignupRegister;
